// settleRound — group play is the frame; settle each SELECTED game type over the
// whole field (all groups' scores merged), net or gross per the Use Handicap master
// switch. Reuses the GGC engines. Returns per-game standings + money, plus a
// combined per-player total and a who-owes-whom settle-up.
import { computeSkins, settleSkins } from './ggPlaySkins.js'
import { settleScatsGame } from './ggPlayScats.js'
import { calcStroke } from './ggStrokePlay.js'
import { computeTeamMatchResults } from './ggTeamCup.js'
import { strokesPerHoleForMode, DEFAULT_STROKE_INDEX } from './ggHandicap.js'

export const ROUND_GAMES = [
  { id: 'skins', label: 'Skins', team: false },
  { id: 'scats', label: 'Scats', team: false },
  { id: 'scramble', label: 'Scramble', team: true },
  { id: 'match', label: 'Match Play', team: false },
  { id: 'stroke', label: 'Stroke Play', team: false },
  { id: 'fourball', label: 'Four Ball', team: true },
  { id: 'shamble', label: 'Shamble', team: true },
  { id: 'practice', label: 'Practice Round', team: false },
]
export const isTeamGame = (id) => !!ROUND_GAMES.find((g) => g.id === id)?.team
export const anyTeamGame = (games) => (games || []).some(isTeamGame)
export const gameLabel = (id) => ROUND_GAMES.find((g) => g.id === id)?.label || id

const numv = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null }

// Merge every group's scores → { hole: { pid: gross } }
function mergeScores(round) {
  const out = {}
  for (const m of Object.values(round.matches || {})) {
    const sc = m.scores || {}
    for (const h of Object.keys(sc)) { out[h] = { ...(out[h] || {}), ...(sc[h] || {}) } }
  }
  return out
}
// Per-player 18-length score array (net when useNet, using the game's stroke mode).
function scoreArrays(players, byHole, useNet, mode, si) {
  const field = players.map((p) => ({ playingHandicap: p.playingHandicap || 0, courseHandicap: p.courseHandicap || 0 }))
  const strokes = players.map((p, i) => (useNet ? strokesPerHoleForMode(mode || 'full', field[i], field, si) : Array(18).fill(0)))
  const map = {}
  players.forEach((p, i) => {
    map[p.id] = Array.from({ length: 18 }, (_, h) => { const g = numv(byHole?.[h + 1]?.[p.id]); return g == null ? null : g - (strokes[i][h] || 0) })
  })
  return map
}
// greedy who-owes-whom
export function settleUp(rows) {
  const cr = rows.filter((r) => Math.round(r.net) > 0).map((r) => ({ name: r.name, rem: Math.round(r.net) })).sort((a, b) => b.rem - a.rem)
  const de = rows.filter((r) => Math.round(r.net) < 0).map((r) => ({ name: r.name, rem: -Math.round(r.net) })).sort((a, b) => b.rem - a.rem)
  const tx = []; let ci = 0, di = 0
  while (ci < cr.length && di < de.length) { const c = cr[ci], d = de[di]; const a = Math.min(c.rem, d.rem); if (a > 0) tx.push({ from: d.name, to: c.name, amt: a }); c.rem -= a; d.rem -= a; if (c.rem <= 0) ci++; if (d.rem <= 0) di++ }
  return tx
}

export function settleRound(round) {
  const players = round.players || []
  const nameById = Object.fromEntries(players.map((p) => [p.id, p.name]))
  const byHole = mergeScores(round)
  const pars = round.course?.pars || Array(18).fill(4)
  const si = round.course?.strokeIndex || DEFAULT_STROKE_INDEX
  const useHcp = !!round.useHandicap
  const opts = round.gameOpts || {}
  const games = (round.games || []).filter((g) => g !== 'practice' || (round.games || []).length === 1)
  const out = { games: {}, total: {} }
  players.forEach((p) => { out.total[p.id] = 0 })
  const add = (pid, v) => { out.total[pid] = (out.total[pid] || 0) + (v || 0) }

  for (const gid of games) {
    const o = opts[gid] || {}
    const useNet = useHcp && o.useHandicap !== false && gid !== 'practice'
    const arrs = scoreArrays(players, byHole, useNet, o.mode || 'cap1', si)

    if (gid === 'skins') {
      const names = players.map((p) => p.name)
      const grid = players.map((p) => arrs[p.id])
      const inArr = players.map(() => true)
      const amount = Number(o.amount) || 0
      const res = computeSkins(names, grid, 19, inArr, o.carry !== false, !!o.double)
      const nets = amount > 0 ? settleSkins(res, amount, inArr, o.payout === 'per-player' ? 'per-player' : 'split-pot') : players.map(() => 0)
      out.games.skins = { label: 'Skins', net: !!useNet, standings: players.map((p, i) => ({ name: p.name, detail: `${res.counts?.[i] ?? 0} skin${(res.counts?.[i] ?? 0) === 1 ? '' : 's'}`, net: Math.round(nets[i]) })).sort((a, b) => b.net - a.net) }
      players.forEach((p, i) => add(p.id, Math.round(nets[i])))
    } else if (gid === 'scats') {
      const sc = settleScatsGame(players, byHole, useNet ? pars : pars, { buyIn: Number(o.buyIn) || 10, useHandicap: useNet, mode: o.mode || 'cap1', strokeIndex: si, optedIn: players.map((p) => p.id) })
      out.games.scats = { label: 'Scats', net: !!useNet, standings: sc.rows.map((r) => ({ name: r.name, detail: `${r.scats} scat${r.scats === 1 ? '' : 's'}`, net: Math.round(r.net) })) }
      sc.rows.forEach((r) => add(r.id, Math.round(r.net)))
    } else if (gid === 'stroke' || gid === 'practice') {
      const rows = calcStroke(players.map((p) => ({ id: p.id, name: p.name, scores: arrs[p.id] })))
      const bet = gid === 'practice' ? 0 : (Number(o.bet) || 0)
      // low total wins bet from each other (only when a bet is set)
      let moneyByPid = {}
      if (bet > 0 && rows.length > 1 && rows[0].total != null) {
        const low = rows[0].total; const winners = rows.filter((r) => r.total === low); const losers = rows.filter((r) => r.total > low)
        losers.forEach((r) => { moneyByPid[r.id] = (moneyByPid[r.id] || 0) - bet }); const pot = losers.length * bet
        winners.forEach((r) => { moneyByPid[r.id] = (moneyByPid[r.id] || 0) + Math.round(pot / winners.length) })
      }
      out.games[gid] = { label: gid === 'practice' ? 'Practice' : 'Stroke Play', net: !!useNet, standings: rows.map((r) => ({ name: r.name, detail: `${r.total} total`, net: Math.round(moneyByPid[r.id] || 0) })) }
      rows.forEach((r) => add(r.id, Math.round(moneyByPid[r.id] || 0)))
    } else if (gid === 'match') {
      // individual match play across the field: per hole, low net wins; holes won × bet, zero-sum pairwise
      const bet = Number(o.bet) || 0
      const won = {}; players.forEach((p) => { won[p.id] = 0 })
      for (let h = 0; h < 18; h++) {
        const vals = players.map((p) => ({ id: p.id, s: arrs[p.id][h] })).filter((x) => x.s != null)
        if (vals.length < 2) continue
        const min = Math.min(...vals.map((x) => x.s)); const w = vals.filter((x) => x.s === min)
        if (w.length === 1) won[w[0].id] += 1
      }
      const totalW = Object.values(won).reduce((a, b) => a + b, 0); const n = players.length
      out.games.match = { label: 'Match Play', net: !!useNet, standings: players.map((p) => ({ name: p.name, detail: `${won[p.id]} holes`, net: bet > 0 ? Math.round((n * won[p.id] - totalW) * bet / n) : 0 })).sort((a, b) => b.net - a.net) }
      if (bet > 0) players.forEach((p) => add(p.id, Math.round((n * won[p.id] - totalW) * bet / n)))
    } else if (isTeamGame(gid)) {
      // Team games (Four Ball / Scramble / Shamble): Team A vs Team B best-ball match.
      const teams = ['A', 'B'].map((t) => ({ id: t, playerIds: players.filter((p) => p.team === t).map((p) => p.id) }))
      const strokesByPlayer = Object.fromEntries(players.map((p) => [p.id, useNet ? strokesPerHoleForMode(o.mode || 'cap1', { playingHandicap: p.playingHandicap }, players.map((x) => ({ playingHandicap: x.playingHandicap })), si) : Array(18).fill(0)]))
      const r = computeTeamMatchResults({ teams, scoresByHole: byHole, strokesByPlayer, handicapsOn: useNet })
      const a = r.holePoints.A || 0, b = r.holePoints.B || 0
      const bet = Number(o.bet) || 0
      const status = a === b ? 'All square' : a > b ? `Team A wins ${a}–${b}` : `Team B wins ${b}–${a}`
      const winner = a === b ? null : a > b ? 'A' : 'B'
      // Points go in the detail; the money column shows the team's DOLLAR result (zero-sum).
      const dollar = { A: 0, B: 0 }
      if (bet > 0 && winner) {
        const loser = winner === 'A' ? 'B' : 'A'
        const wIds = teams.find((t) => t.id === winner).playerIds
        const lIds = teams.find((t) => t.id === loser).playerIds
        const pot = lIds.length * bet
        dollar[winner] = pot; dollar[loser] = -pot
        wIds.forEach((id) => add(id, Math.round(pot / Math.max(1, wIds.length))))
        lIds.forEach((id) => add(id, -bet))
      }
      out.games[gid] = { label: gameLabel(gid), net: !!useNet, team: true, status, standings: [
        { name: 'Team A', detail: `${a} pts · ${teams[0].playerIds.map((id) => nameById[id]).join(' / ')}`, net: dollar.A },
        { name: 'Team B', detail: `${b} pts · ${teams[1].playerIds.map((id) => nameById[id]).join(' / ')}`, net: dollar.B },
      ] }
    }
  }

  // "No bets" round: results still compute + show, but there's no money at all —
  // zero every per-game net, per-player total, and the who-owes-whom settle-up.
  if (round.noBets) {
    for (const g of Object.values(out.games)) { if (Array.isArray(g.standings)) g.standings.forEach((s) => { s.net = 0 }) }
    players.forEach((p) => { out.total[p.id] = 0 })
    out.noBets = true
  }

  const totalRows = players.map((p) => ({ id: p.id, name: p.name, net: Math.round(out.total[p.id] || 0) })).sort((x, y) => y.net - x.net)
  out.totalRows = totalRows
  out.settleUp = settleUp(totalRows)
  return out
}
